import { Braces, CircleCheck, CircleX, Circle, Flag, Info, TriangleAlert } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatLogTime } from "../lib/format";
import { AppLink } from "../primitives/link";
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from "../primitives/menu";
import { CopyButton } from "./copy";

/** Stat grid: 2-4 equal figures in one row, each in its own bordered tile (ui-spec §3.4.1). */
export function StatGrid({ children }: { children: React.ReactNode }) {
  return <div className="nd-stat-grid grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))] gap-[var(--nd-stat-grid-gap)]">{children}</div>;
}

export function StatCard({
  label,
  value,
  unit,
  caption,
  href,
  unknownReason,
  live,
  valueSize = "metric",
}: {
  label: string;
  /** Text identities and configuration values use body-sized type instead of numeric emphasis. */
  valueSize?: "metric" | "text";
  value: React.ReactNode | undefined;
  unit?: React.ReactNode;
  caption?: React.ReactNode;
  href?: string;
  /** Why the value is unknown; unknown values render `—`, never 0. */
  unknownReason?: string;
  /** Flashes once when the value changes from a watch event. */
  live?: boolean;
}) {
  const [flash, setFlash] = React.useState(false);
  const previous = React.useRef(value);
  React.useEffect(() => {
    if (!live || previous.current === value) return;
    previous.current = value;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 700);
    return () => clearTimeout(t);
  }, [value, live]);
  const shown =
    value === undefined || value === null ? (
      <Tooltip content={unknownReason ?? "Not reported yet"}>
        <span>—</span>
      </Tooltip>
    ) : (
      value
    );
  return (
    <div className="nd-stat-card relative min-w-0 rounded-lg border border-line bg-card px-4 py-3" data-live-updated={flash} data-value-size={valueSize}>
      <div className="text-small text-fg-muted">
        {href ? (
          <AppLink href={href} className="nd-stretched hover:text-fg-primary">
            {label}
          </AppLink>
        ) : (
          label
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-stat-value text-fg-primary tabular-nums">{shown}</span>
        {unit ? <span className="text-small text-fg-muted">{unit}</span> : null}
      </div>
      {caption ? <div className="mt-0.5 text-small text-fg-muted">{caption}</div> : null}
    </div>
  );
}

export interface KeyValueRow {
  label: string;
  value: React.ReactNode;
  /** The API field path, shown by the `{}` field hint (ui-spec §4.9). */
  field?: string;
  /** Raw value for the field hint and Copy. */
  raw?: string;
  mono?: boolean;
  /** Command for "Copy nodus explain …". */
  explain?: string;
}

/** Field hint: a real button in the tab order that shows the API field path and raw value. */
export function FieldHint({ field, raw, explain }: { field: string; raw?: string; explain?: string }) {
  return (
    <Popover>
      <PopoverTrigger
        aria-label={`API field ${field}`}
        className="inline-flex size-6 items-center justify-center rounded-sm text-fg-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-fg-primary"
      >
        <Braces className="size-3.5" aria-hidden />
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-md p-3">
        <div className="flex items-center gap-2">
          <code className="font-mono text-mono text-fg-body">
            {field}
            {raw !== undefined ? `: ${raw}` : ""}
          </code>
          <CopyButton text={raw !== undefined ? `${field}: ${raw}` : field} label="Copy field" />
        </div>
        {explain ? (
          <div className="mt-2 flex items-center gap-2 text-small text-fg-muted">
            <code className="font-mono">{explain}</code>
            <CopyButton text={explain} label="Copy explain command" />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** One row per field on hairlines, no box, label column 160-200px (ui-spec §3.4.2). */
export function KeyValueTable({ rows }: { rows: KeyValueRow[] }) {
  return (
    <dl className="nd-key-values border-t border-line-subtle" data-focus-inset>
      {rows.map((r) => (
        <div
          key={r.label}
          className="group grid min-h-[var(--nd-kv-row-height)] grid-cols-[minmax(5rem,min(200px,40%))_minmax(0,1fr)] items-center gap-4 border-b border-line-subtle focus-within:bg-hover"
        >
          <dt className="text-ui text-fg-muted">{r.label}</dt>
          <dd className="flex min-w-0 items-center gap-2">
            <span className={cn("min-w-0 flex-1 text-ui break-words text-fg-secondary", r.mono && "font-mono text-mono")}>{r.value}</span>
            {/* Only rows with a hint or a copy action reserve room for them, so a narrow aside keeps its values readable. */}
            {r.field || r.raw !== undefined ? (
              <span className="flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                {r.field ? <FieldHint field={r.field} raw={r.raw} explain={r.explain} /> : null}
                {r.raw !== undefined ? <CopyButton text={r.raw} label={`Copy ${r.label}`} /> : null}
              </span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface ActivityItem {
  id: string;
  time: string;
  type: "Normal" | "Warning" | "Milestone";
  message: string;
  source?: string;
}

const eventIcon = {
  Normal: { icon: Info, label: "Event", dot: "bg-neutral-dot" },
  Milestone: { icon: Flag, label: "Milestone", dot: "bg-copper" },
  Warning: { icon: TriangleAlert, label: "Warning", dot: "bg-danger-dot" },
} as const;

/** A timestamp shown compactly when it parses as one; other strings (a clock already formatted) pass through. */
function eventTime(time: string): string {
  return /^\d{4}-\d{2}-\d{2}T/.test(time) ? formatLogTime(time) : time;
}

/** Events newest first; the type is an icon with a name, and warnings also carry the word (ui-spec §3.4.3). */
export function ActivityFeed({ items, limit }: { items: ActivityItem[]; limit?: number }) {
  const shown = limit ? items.slice(0, limit) : items;
  return (
    <ol className="border-t border-line-subtle">
      {shown.map((e) => {
        const t = eventIcon[e.type];
        const Icon = t.icon;
        return (
          <li
            key={e.id}
            className="grid min-h-[var(--nd-activity-row-height)] grid-cols-[120px_16px_1fr_auto] items-center gap-3 border-b border-line-subtle"
          >
            <time dateTime={e.time} className="text-small text-fg-muted tabular-nums">{eventTime(e.time)}</time>
            <Icon className={cn("size-4", e.type === "Warning" ? "text-warning-fg" : "text-fg-muted")} aria-label={t.label} />
            <span className="min-w-0 text-ui text-fg-secondary">
              {e.type === "Warning" ? <span className="mr-2 text-small text-warning-fg">Warning</span> : null}
              {e.message}
            </span>
            <span className="text-small text-fg-muted">{e.source}</span>
          </li>
        );
      })}
    </ol>
  );
}

export interface CheckItem {
  label: string;
  state: "passed" | "failed" | "pending";
  evidence?: React.ReactNode;
  href?: string;
}

/** Completion evidence: each check carries its proof on the right (P5, ui-spec §3.4.5). */
export function ResultsChecklist({ items, caption }: { items: CheckItem[]; caption?: React.ReactNode }) {
  return (
    <div>
      <ul className="border-t border-line-subtle">
        {items.map((c) => {
          const Icon = c.state === "passed" ? CircleCheck : c.state === "failed" ? CircleX : Circle;
          const tone =
            c.state === "passed" ? "text-success-dot" : c.state === "failed" ? "text-danger-dot" : "text-fg-faint";
          const evidence = c.state === "pending" ? (c.evidence ?? (c.href ? "Start" : "Waiting")) : c.evidence;
          return (
            <li
              key={c.label}
              className="flex min-h-[var(--nd-kv-row-height)] items-center gap-3 border-b border-line-subtle"
            >
              <Icon className={cn("size-4 shrink-0", tone)} aria-label={c.state} />
              <span className="flex-1 text-ui text-fg-secondary">{c.label}</span>
              <span className={cn("text-small", c.state === "failed" ? "text-danger-fg" : "text-fg-muted")}>
                {c.href ? (
                  <AppLink href={c.href} className="hover:text-fg-primary">
                    {evidence}
                  </AppLink>
                ) : (
                  evidence
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {caption ? <p className="mt-3 text-small text-fg-muted">{caption}</p> : null}
    </div>
  );
}

export interface ConditionRow {
  type: string;
  status: "True" | "False" | "Unknown";
  reason?: string;
  message?: string;
  lastTransitionTime?: string;
}

/**
 * The Overview "Health" block (ui-spec §3.4.9): shown only when a condition is not in its good state, with the
 * reason code (copyable) and a link to its generated error page.
 */
export function ConditionsTable({ conditions, docsBase, good = {} }: { conditions: ConditionRow[]; docsBase: string; good?: Record<string, "True" | "False"> }) {
  const bad = conditions.filter((c) => c.status !== (good[c.type] ?? "True"));
  if (bad.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-card">
      <table className="w-full text-ui">
        <caption className="sr-only">Conditions that need attention</caption>
        <thead className="bg-subtle text-small text-fg-muted">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-normal">Condition</th>
            <th scope="col" className="px-3 py-2 text-left font-normal">Status</th>
            <th scope="col" className="px-3 py-2 text-left font-normal">Reason</th>
            <th scope="col" className="px-3 py-2 text-left font-normal">Message</th>
          </tr>
        </thead>
        <tbody>
          {bad.map((c) => (
            <tr key={c.type} className="border-t border-line-subtle">
              <td className="px-3 py-2 text-fg-body">{c.type}</td>
              <td className="px-3 py-2 text-fg-secondary">{c.status === "Unknown" ? "Unknown" : c.status === "True" ? "Yes" : "No"}</td>
              <td className="px-3 py-2">
                {c.reason ? (
                  <span className="inline-flex items-center gap-1">
                    <a href={`${docsBase}/errors/${c.reason}`} target="_blank" rel="noopener noreferrer" className="font-mono text-mono text-fg-secondary underline-offset-2 hover:underline">
                      {c.reason}
                      <span className="sr-only"> (opens the error reference in a new tab)</span>
                    </a>
                    <CopyButton text={c.reason} label="Copy reason" />
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-2 text-fg-secondary">{c.message ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
